import { createSelector } from '@reduxjs/toolkit'
import { ApplicationState } from 'redux/Stores/types'
import { ApiKey, ApiKeyType } from 'types/GlobalApiKey'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

export const selectExportTableApiKeys = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) =>
      Object.values(state.ApiKeyStore.data).filter(
        (apiKey) => apiKey.type === ApiKeyType.EXPORT_TABLE
      ),
    (state: ApplicationState) => state.ApiKeyStore.fetched
  ],
  (apiKeys, fetched): { keys: ApiKey[]; fetched: boolean } => ({
    keys: [...apiKeys].sort((a, b) => sortAlphabeticalAsc(a.name, b.name)),
    fetched
  })
)

type AccessToItem = { id: string; title: string }

export const selectApiKeys = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => Object.values(state.ApiKeyStore.data),
    (state: ApplicationState) => state.ApiKeyStore.fetched,
    (state: ApplicationState) => Object.values(state.ExportTableStore.data),
    (state: ApplicationState) =>
      Object.values(state.KPIDashboardStore.dashboards)
  ],
  (
    apiKeys,
    fetched,
    exportTables,
    dashboards
  ): {
    keys: ApiKey[]
    fetched: boolean
    accessTo: Record<string, AccessToItem[]>
  } => {
    // Build accessTo map: apiKeyId -> [{id, title}]
    const accessTo: Record<string, AccessToItem[]> = {}

    // Dashboards with API access enabled
    const apiAccessDashboards = dashboards.filter(
      (dashboard) => dashboard.share_settings?.api_access
    )

    for (const apiKey of apiKeys) {
      accessTo[apiKey.id] = []

      // DASHBOARD keys get access to all dashboards with api_access enabled
      if (apiKey.type === ApiKeyType.DASHBOARD) {
        for (const dashboard of apiAccessDashboards) {
          accessTo[apiKey.id].push({
            id: dashboard.id,
            title: dashboard.title
          })
        }
      }
    }

    // EXPORT_TABLE keys get access based on export_table.api_keys
    for (const exportTable of exportTables) {
      for (const apiKeyId of exportTable.api_keys) {
        if (accessTo[apiKeyId] && exportTable.slug) {
          accessTo[apiKeyId].push({
            id: exportTable.id,
            title: exportTable.title
          })
        }
      }
    }

    return { keys: apiKeys, fetched, accessTo }
  }
)
